import {
  AiBrain01Icon,
  Calendar03Icon,
  Compass01Icon,
  DashboardSquare01Icon,
  Database01Icon,
  Globe02Icon,
  Settings02Icon,
  SparklesIcon,
  TestTube01Icon,
  UserMultiple02Icon,
} from '@hugeicons/core-free-icons';
import { PLATFORMS } from '../platform/platformConfig';

/**
 * Guided tours. A step points at one element:
 * - `target`: a `data-tour` value. Sidebar items are `nav-<navConfig key>` (NavMenu adds them);
 *   the shell has `search`, `help` and `account`.
 * - `selector`: any CSS selector. Every page gets `[data-slot="page-header"]`,
 *   `[data-slot="page-header-actions"]` and `[data-slot="page-toolbar"]` from its PageHeader
 *   and PageToolbar, so most area tours need no anchors of their own.
 * - neither: a centred card, for an intro or a closing note.
 * `path` sends the tour to another page before the step. `optional` steps are skipped when
 * their element isn't on the page (a hidden section, an empty page); others fall back to a
 * centred card, as do sidebar steps on phones (`phoneBody` can reword a step there).
 * `sectionname` drops a step when the team can't open that section.
 * Where a tour belongs:
 * - `path`: an area tour. It runs on that page and can be started from anywhere (it goes there).
 * - `match(ctx)`: a screen tour. It runs on whatever page matches, and only there; `ctx` is
 *   { pathname, agentStep, agentType, engStep, ctaStep, endScreenStep } (see useTourContext),
 *   so a tour can follow one wizard step. `entry` is where the Help menu can open it from
 *   elsewhere; without one, `needs` says what to open first.
 * - `group` sorts it in Help → Guided tours. `prompt: false` keeps it out of the top bar's "Take a tour" button.
 * Copy: sentence case, UK English, one or two short lines that say what the thing is for.
 */

const HEADER = '[data-slot="page-header"]';
const ACTIONS = '[data-slot="page-header-actions"]';
const TOOLBAR = '[data-slot="page-toolbar"]';

const STEPS = '[data-slot="steps"]';
const FOOTER = '[data-slot="step-footer"]';
const SECTION = '[data-slot="section-card"]';

const AGENT_EDITOR = ['/agents/create', '/agents/edit', '/agents/draft'];
const ENG_EDITOR = ['/engagement/create', '/engagement/edit', '/engagement/draft'];
const CTA_EDITOR = ['/cta/create', '/cta/edit', '/cta/draft'];
const END_EDITOR = ['/endscreen/create', '/endscreen/edit', '/endscreen/draft'];
const ENGINE_EDITOR = ['/aiLabs/create', '/aiLabs/edit'];
const on = (paths, ctx) => paths.includes(ctx.pathname);
const isPublishStep = ctx => ctx.agentStep === (ctx.agentType === 'aiAgent' ? 2 : 3);

const shellSteps = [
  {
    target: 'search',
    title: 'Search or jump anywhere',
    body: 'Press ⌘K (Ctrl K on Windows) to open any page, create something or change a setting without leaving the keyboard.',
  },
  {
    target: 'help',
    title: 'Help is always here',
    body: 'Ask AI, read the docs or message support. Guided tours live here too, so you can take any of them again.',
    placement: 'bottom',
  },
  {
    target: 'account',
    title: 'Your account',
    body: 'Your profile, team, integrations and credit usage are under Settings. Your profile opens from here.',
    placement: 'right',
  },
];

const TOURS = [
  {
    id: 'welcome',
    group: 'Get started',
    title: 'Welcome tour',
    description: 'Where everything lives',
    icon: Compass01Icon,
    path: '/',
    platforms: [PLATFORMS.MEDIA],
    steps: [
      {
        title: 'Here’s your workspace',
        body: 'A one-minute look at where things live. Use the arrow keys or the buttons below to move through it.',
        phoneBody:
          'A one-minute look at where things live. On a phone, every section is in the menu at the top left.',
      },
      {
        target: 'nav-agents-parent',
        sectionname: 'campaigns',
        title: 'Agents',
        body: 'Agents answer visitor questions, qualify leads and run on your sites. Start here when you want something live.',
        placement: 'right',
      },
      {
        target: 'nav-screens',
        sectionname: 'screens',
        title: 'Screens',
        body: 'Engagements, CTAs and end screens are what visitors see. Build them once and reuse them across agents.',
        placement: 'right',
      },
      {
        target: 'nav-collections-parent',
        sectionname: 'screens',
        title: 'Collections',
        body: 'The Event Database holds what your agents work from: content, attendees and the programme. Recirculation picks what to read next.',
        placement: 'right',
      },
      {
        target: 'nav-6',
        sectionname: 'website',
        title: 'Websites',
        body: 'Add the domains your agents run on and choose which pages they cover.',
        placement: 'right',
      },
      {
        target: 'nav-aiLabs-parent',
        sectionname: 'aiLabs',
        title: 'AI Labs',
        body: 'Build and test the engines behind your agents, and fine-tune how they answer.',
        placement: 'right',
      },
      {
        target: 'nav-leads',
        sectionname: 'leads',
        title: 'Leads',
        body: 'Everyone your agents capture, in one table you can filter and export.',
        placement: 'right',
      },
      ...shellSteps,
    ],
  },
  {
    id: 'welcome-events',
    group: 'Get started',
    title: 'Welcome tour',
    description: 'Where everything lives',
    icon: Compass01Icon,
    path: '/',
    platforms: [PLATFORMS.EVENTS],
    steps: [
      {
        title: 'Here’s your workspace',
        body: 'A one-minute look at where things live. Use the arrow keys or the buttons below to move through it.',
        phoneBody:
          'A one-minute look at where things live. On a phone, every section is in the menu at the top left.',
      },
      {
        target: 'nav-events',
        sectionname: 'campaigns',
        title: 'Events',
        body: 'Each event holds its workflows, agents, branding and leads. Start here to set one up.',
        placement: 'right',
      },
      {
        target: 'nav-collections',
        sectionname: 'screens',
        title: 'Event Database',
        body: 'The content, attendees and sessions your agents work from.',
        placement: 'right',
      },
      {
        target: 'nav-aiLabs-parent',
        sectionname: 'aiLabs',
        title: 'AI Labs',
        body: 'Build and test the engines behind your agents, and fine-tune how they answer.',
        placement: 'right',
      },
      {
        target: 'nav-leads',
        sectionname: 'leads',
        title: 'Leads',
        body: 'Every registrant and visitor your agents capture, in one table.',
        placement: 'right',
      },
      ...shellSteps,
    ],
  },
  {
    id: 'agents',
    group: 'Agents',
    title: 'Agents',
    description: 'Create, organise and track agents',
    icon: AiBrain01Icon,
    path: '/agents/dashboard',
    platforms: [PLATFORMS.MEDIA],
    sectionname: 'campaigns',
    steps: [
      {
        selector: HEADER,
        title: 'All your agents',
        body: 'Every agent you’ve built, live or paused. Open one to see how it’s doing.',
      },
      {
        selector: ACTIONS,
        optional: true,
        title: 'Create an agent',
        body: 'Pick a goal, design what visitors see, add knowledge, then publish. Unfinished agents wait in Drafts.',
      },
      {
        selector: TOOLBAR,
        optional: true,
        title: 'Find the right one',
        body: 'Switch between list and cards, filter by status or folder, and search by name.',
      },
    ],
  },
  {
    id: 'screens',
    group: 'Screens',
    title: 'Screens',
    description: 'Engagements, CTAs and end screens',
    icon: DashboardSquare01Icon,
    path: '/engagement/dashboard',
    platforms: [PLATFORMS.MEDIA],
    sectionname: 'screens',
    steps: [
      {
        selector: HEADER,
        title: 'Engagements',
        body: 'Polls, quizzes and slider polls that get visitors to interact. Each one can be reused across agents.',
      },
      {
        selector: ACTIONS,
        optional: true,
        title: 'Start from an example',
        body: 'Create an engagement and pick one of the examples to begin with real wording you can edit.',
      },
      {
        path: '/cta/dashboard',
        selector: HEADER,
        title: 'CTAs',
        body: 'Calls to action that collect emails, open a link or show a form, right after an engagement.',
      },
      {
        path: '/endscreen/dashboard',
        selector: HEADER,
        title: 'End screens',
        body: 'The last thing a visitor sees: a thank-you, with the option to share.',
      },
    ],
  },
  {
    id: 'knowledge',
    group: 'Event Database',
    title: 'Event Database',
    description: 'What your agents work from',
    icon: Database01Icon,
    path: '/collections/dashboard',
    sectionname: 'screens',
    steps: [
      {
        selector: HEADER,
        title: 'Your Event Database',
        body: 'Knowledge bases of pages, files and notes, attendee collections and event collections. Agents use only the collections you give them.',
      },
      {
        selector: ACTIONS,
        optional: true,
        title: 'Add a collection',
        body: 'Add links, upload files or write notes for your agents to draw on.',
      },
      {
        selector: TOOLBAR,
        optional: true,
        title: 'Keep it organised',
        body: 'Search and filter as the list grows.',
      },
    ],
  },
  {
    id: 'domains',
    group: 'Websites',
    title: 'Domains',
    description: 'Where your agents run',
    icon: Globe02Icon,
    path: '/domains',
    platforms: [PLATFORMS.MEDIA],
    sectionname: 'website',
    steps: [
      {
        selector: HEADER,
        title: 'Your domains',
        body: 'The sites your agents can run on. Add a domain once and every agent can use it.',
      },
      {
        selector: ACTIONS,
        optional: true,
        title: 'Add a domain',
        body: 'Enter the address and choose its content settings.',
      },
    ],
  },
  {
    id: 'aiLabs',
    group: 'AI Labs',
    title: 'AI Labs',
    description: 'Engines and fine tuning',
    icon: SparklesIcon,
    path: '/aiLabs/dashboard',
    sectionname: 'aiLabs',
    steps: [
      {
        selector: HEADER,
        title: 'Engines',
        body: 'The engines your agents run on: what they know and how they answer.',
      },
      {
        selector: ACTIONS,
        optional: true,
        title: 'Create an engine',
        body: 'Start from one of Bridged’s engines or build your own, and try it in the simulator before an agent uses it.',
      },
      {
        target: 'nav-fine-tuning',
        optional: true,
        title: 'Fine tuning',
        body: 'Train your engines on the feedback your agents collect.',
        placement: 'right',
      },
    ],
  },
  {
    id: 'abTest',
    group: 'Agents',
    title: 'A/B tests',
    description: 'Compare two versions',
    icon: TestTube01Icon,
    path: '/abTest',
    sectionname: 'campaignABTest',
    steps: [
      {
        selector: HEADER,
        title: 'A/B tests',
        body: 'Run two versions side by side and see which one gets more interaction and leads.',
      },
      {
        selector: ACTIONS,
        optional: true,
        title: 'Start a test',
        body: 'Pick an agent. We copy it to make version B, which you edit next.',
      },
    ],
  },
  {
    id: 'events',
    group: 'Events',
    title: 'Events',
    description: 'Set up and run an event',
    icon: Calendar03Icon,
    path: '/events',
    platforms: [PLATFORMS.EVENTS],
    sectionname: 'campaigns',
    steps: [
      {
        selector: HEADER,
        title: 'Your events',
        body: 'Upcoming, live and past events. Open one to manage its workflows, agents, branding and leads.',
      },
      {
        selector: ACTIONS,
        optional: true,
        title: 'Create an event',
        body: 'Add the dates and place, then choose the workflows you want to run.',
      },
      {
        selector: TOOLBAR,
        optional: true,
        title: 'Find an event',
        body: 'Filter by status and search by name.',
      },
    ],
  },
  {
    id: 'leads',
    group: 'Leads and settings',
    title: 'Leads',
    description: 'Everyone your agents capture',
    icon: UserMultiple02Icon,
    path: '/leads',
    sectionname: 'leads',
    steps: [
      {
        selector: HEADER,
        title: 'Your leads',
        body: 'Everyone who shared their details with an agent, with where and when they did.',
      },
      {
        selector: TOOLBAR,
        optional: true,
        title: 'Filter and export',
        body: 'Narrow the list down and download it when you need it elsewhere.',
      },
    ],
  },
  {
    id: 'settings',
    group: 'Leads and settings',
    title: 'Settings',
    description: 'Profile, team and integrations',
    icon: Settings02Icon,
    path: '/profile',
    steps: [
      {
        selector: HEADER,
        title: 'Your profile',
        body: 'Your name, photo, company and language.',
      },
      {
        path: '/team',
        selector: HEADER,
        title: 'Your team',
        body: 'Invite people and choose which sections each of them can open.',
      },
      {
        path: '/integrations',
        selector: HEADER,
        title: 'Integrations',
        body: 'Connect your CRM, messaging and webhooks so leads go where you work.',
      },
    ],
  },
  // Screen tours: they run on the screen you're on, and follow wizard steps.
  {
    id: 'agent-goal',
    group: 'Agents',
    title: 'Create an agent',
    description: 'Goal step: pick what it’s for',
    icon: AiBrain01Icon,
    platforms: [PLATFORMS.MEDIA],
    sectionname: 'campaigns',
    match: ctx => on(AGENT_EDITOR, ctx) && ctx.agentStep === 0,
    entry: '/agents/create',
    needs: 'Go back to the Goal step in Create agent',
    steps: [
      {
        selector: STEPS,
        optional: true,
        title: 'A few steps to a live agent',
        body: 'Pick a goal, set it up and choose where it runs, design what visitors see, then publish. You can go back to any step you’ve finished.',
      },
      {
        target: 'agent-goal',
        title: 'Pick what it’s for',
        body: 'Choose a workflow first, then the agent that fits it. Each card says what that agent does.',
        placement: 'top',
      },
      {
        selector: FOOTER,
        optional: true,
        title: 'Move between steps',
        body: 'Next unlocks once you’ve picked an agent. After that, Save as draft at the top keeps your progress for later.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'agent-define',
    group: 'Agents',
    title: 'Set up an agent',
    description: 'Define step: name, placement and setup',
    icon: AiBrain01Icon,
    sectionname: 'campaigns',
    prompt: false,
    match: ctx => on(AGENT_EDITOR, ctx) && ctx.agentStep === 1,
    needs: 'Reach the Define step in Create agent',
    steps: [
      {
        selector: SECTION,
        title: 'Tell it what to work with',
        body: 'Name the agent, choose the pages it appears on and what it works with. Each section says what it changes.',
      },
      {
        selector: FOOTER,
        optional: true,
        title: 'Check, then continue',
        body: 'Next checks the required fields first. Anything missing is pointed out.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'agent-design',
    group: 'Agents',
    title: 'Design an agent',
    description: 'Design step: screens, look and preview',
    icon: AiBrain01Icon,
    sectionname: 'campaigns',
    prompt: false,
    match: ctx => on(AGENT_EDITOR, ctx) && ctx.agentType !== 'aiAgent' && ctx.agentStep === 2,
    needs: 'Reach the Design step in Create agent',
    steps: [
      {
        selector: SECTION,
        title: 'Design what visitors see',
        body: 'Choose the screens it shows and how it looks on your site: colours, fonts, logo and layout.',
      },
      {
        target: 'agent-preview',
        optional: true,
        title: 'Check it as a visitor',
        body: 'The preview updates as you change things. For a side panel, Show on page puts it on your site instead.',
      },
      {
        selector: FOOTER,
        optional: true,
        title: 'Then publish',
        body: 'When it looks right, go on to check everything and choose when it runs.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'agent-publish',
    group: 'Agents',
    title: 'Publish an agent',
    description: 'Publish step: check and schedule',
    icon: AiBrain01Icon,
    sectionname: 'campaigns',
    prompt: false,
    match: ctx => on(AGENT_EDITOR, ctx) && isPublishStep(ctx),
    needs: 'Reach the Publish step in Create agent',
    steps: [
      {
        selector: SECTION,
        title: 'Check and schedule',
        body: 'Review what you’ve set up, then choose when it starts and stops. Anything that needs attention is flagged here.',
      },
      {
        selector: FOOTER,
        optional: true,
        title: 'Go live',
        body: 'Publishing puts it on your site. Until then it’s a draft only your team can see.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'agent-overview',
    group: 'Agents',
    title: 'Agent overview',
    description: 'How one agent is doing',
    icon: AiBrain01Icon,
    sectionname: 'campaigns',
    match: ctx => ctx.pathname === '/agents/overview',
    needs: 'Open an agent first',
    steps: [
      {
        selector: HEADER,
        title: 'This agent at a glance',
        body: 'Its status, type and when it was made. Edit is on the right; More holds the other actions.',
      },
      {
        target: 'overview-period',
        optional: true,
        title: 'Pick a period',
        body: 'Every number on this page follows these dates.',
      },
      {
        selector: '[data-slot="stat-strip"]',
        optional: true,
        title: 'The key numbers',
        body: 'The headline numbers for this type of agent. The sections below break them down.',
      },
    ],
  },
  {
    id: 'screen-type',
    group: 'Screens',
    title: 'Create a screen',
    description: 'Pick a type, or start from an example',
    icon: DashboardSquare01Icon,
    platforms: [PLATFORMS.MEDIA],
    sectionname: 'screens',
    match: ctx =>
      (on(ENG_EDITOR, ctx) && ctx.engStep === 0) || (on(CTA_EDITOR, ctx) && ctx.ctaStep === 0),
    entry: '/engagement/create',
    needs: 'Go back to the type step',
    steps: [
      {
        target: 'screen-type',
        title: 'Pick a type',
        body: 'Each card says what that type is for. Double-click one to go straight to editing it.',
      },
      {
        target: 'eng-examples',
        optional: true,
        title: 'Or start from an example',
        body: 'Ready-made questions for registrants, sessions, exhibitors, sponsors and awards. Edit anything before you publish.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'screen-editor',
    group: 'Screens',
    title: 'Edit a screen',
    description: 'Write it, preview it, publish it',
    icon: DashboardSquare01Icon,
    sectionname: 'screens',
    prompt: false,
    match: ctx =>
      (on(ENG_EDITOR, ctx) && ctx.engStep >= 1) ||
      (on(CTA_EDITOR, ctx) && ctx.ctaStep >= 1) ||
      on(END_EDITOR, ctx),
    needs: 'Start creating an engagement, CTA or end screen',
    steps: [
      {
        target: 'screen-form',
        title: 'Write it',
        body: 'Fill in the title, the text and the options. Required fields are marked.',
        placement: 'right',
      },
      {
        target: 'screen-preview',
        optional: true,
        title: 'See it as visitors will',
        body: 'The preview updates as you type. Switch between desktop and mobile.',
        placement: 'left',
      },
      {
        selector: FOOTER,
        optional: true,
        title: 'Publish it',
        body: 'Publishing makes it ready to add to agents. Save as draft keeps it for later.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'collection',
    group: 'Event Database',
    title: 'Inside a collection',
    description: 'Add, label and keep content fresh',
    icon: Database01Icon,
    sectionname: 'screens',
    match: ctx => ctx.pathname === '/collections/create',
    needs: 'Open a collection first',
    steps: [
      {
        target: 'kb-add-context',
        title: 'Add context',
        body: 'Add links, upload files or write notes. Label each item so agents use it the right way, such as Sessions, Sponsors or Awards.',
      },
      {
        target: 'kb-sync',
        optional: true,
        title: 'Keep it fresh',
        body: 'Sync settings re-read your links on a schedule. Authentication lets us read pages behind a login.',
      },
      {
        selector: '[data-slot="data-table"]',
        optional: true,
        title: 'Everything in this collection',
        body: 'Check each item’s status and open one to see what was read. Select several to remove them together.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'engine',
    group: 'AI Labs',
    title: 'Build an engine',
    description: 'Write guidelines and try them out',
    icon: SparklesIcon,
    sectionname: 'aiLabs',
    match: ctx => on(ENGINE_EDITOR, ctx),
    needs: 'Open or create an engine first',
    steps: [
      {
        target: 'engine-build',
        title: 'Tell it how to answer',
        body: 'Write the guidelines for its answers, FAQs and fallbacks. The core rules always apply on top.',
        placement: 'right',
      },
      {
        target: 'engine-test',
        optional: true,
        title: 'Try it before you ship',
        body: 'Try it as a visitor would and check what it says before any agent uses it.',
        placement: 'left',
      },
    ],
  },
  {
    id: 'event',
    group: 'Events',
    title: 'Inside an event',
    description: 'Workflows, branding and leads',
    icon: Calendar03Icon,
    platforms: [PLATFORMS.EVENTS],
    sectionname: 'campaigns',
    match: ctx => /^\/events\/[^/]+\/workflows$/.test(ctx.pathname),
    needs: 'Open an event first',
    steps: [
      {
        selector: HEADER,
        title: 'Your event',
        body: 'Its dates, place and status. Edit event changes the details.',
      },
      {
        selector: `${HEADER} [role="tablist"]`,
        optional: true,
        title: 'Everything for this event',
        body: 'Workflows, branding and leads each have their own tab.',
      },
      {
        title: 'Workflows run the event',
        body: 'Each workflow groups the agents for one goal, such as registrations, sponsors or awards. Open one to add or change its agents.',
      },
    ],
  },
  {
    id: 'event-workflow',
    group: 'Events',
    title: 'Inside a workflow',
    description: 'The agents behind one goal',
    icon: Calendar03Icon,
    platforms: [PLATFORMS.EVENTS],
    sectionname: 'campaigns',
    match: ctx => /^\/events\/[^/]+\/workflows\/[^/]+$/.test(ctx.pathname),
    needs: 'Open a workflow inside an event',
    steps: [
      {
        target: 'event-workflow',
        title: 'One workflow',
        body: 'What this workflow is for. The agents that work towards it are listed below, with whether each one is live.',
      },
      {
        selector: TOOLBAR,
        optional: true,
        title: 'Find an agent',
        body: 'Filter and search as the list grows.',
      },
    ],
  },
];

const allowed = (sectionname, accessibleSection) =>
  !sectionname || !accessibleSection?.length || accessibleSection.includes(sectionname);

/** The tours this team can take on this platform, with steps it can't see left out. */
export function getTours({ platform, accessibleSection }) {
  return TOURS.filter(
    tour =>
      (!tour.platforms || tour.platforms.includes(platform)) &&
      allowed(tour.sectionname, accessibleSection)
  ).map(tour => ({
    ...tour,
    steps: tour.steps.filter(step => allowed(step.sectionname, accessibleSection)),
  }));
}

export function getWelcomeTour(tours) {
  return tours.find(tour => tour.id === 'welcome' || tour.id === 'welcome-events');
}

/** True when the tour belongs to the screen described by `ctx` (see useTourContext). */
export function tourMatches(tour, ctx) {
  if (tour.match) return tour.match(ctx);
  if (tour.id.startsWith('welcome')) return false;
  return tour.path === ctx.pathname || tour.steps.some(step => step.path === ctx.pathname);
}

/** Every tour for this screen, screen tours (the most specific) first. */
export function getToursForContext(tours, ctx) {
  const matching = tours.filter(tour => tourMatches(tour, ctx));
  return [...matching.filter(t => t.match), ...matching.filter(t => !t.match)];
}

/** Where a tour can be started from another page, or null when it needs a specific record. */
export function getTourEntry(tour) {
  return tour.path || tour.entry || null;
}
